import { FeedbackType, IDatahubOrderLine, ISimplifiedOrder } from '../orders/models';
import { ISimplifiedVisualMaterial } from '../material/models';
import { PaginatedListResponseDeprecatedType, CommonGetListType } from '../types';
import { IDatahubTaskDto } from '../tasks/dtos';
import { IBranch, IUser } from '../users/models';
import { IDatahubCase } from '../cases/models';

export enum TaskStatus {
  Complete = 'COMPLETE',
  Partial = 'PARTIAL',
  Open = 'NOT_DONE',
  Unknown = 'UNKNOWN',
  Cancelled = 'CANCELLED',
}

export enum TaskCategory {
  BookPhotographer = 'BOOK_PHOTOGRAPHER',
  UploadImages = 'UPLOAD_IMAGES',
  UploadVideos = 'UPLOAD_VIDEOS',
  VirtualStaging = 'VIRTUAL_STAGING',
  CopywriteTexts = 'COPYWRITE_TEXTS',
  Location = 'LOCATION',
  Person = 'PERSON',
  Basic = 'BASIC',
  IntroOutroSpeak = 'INTRO_OUTRO_SPEAK',
  BasicOpt = 'BASIC_OPT',
  Text = 'TEXT',
  VirtualStagingRemoveObjects = 'VIRTUAL_STAGING_REMOVE_OBJECTS',
  VirtualStagingIndoorRenovation = 'VIRTUAL_STAGING_INDOOR_RENOVATION',
  VirtualStagingOutdoorRenovation = 'VIRTUAL_STAGING_OUTDOOR_RENOVATION',
  VirtualStaging720 = 'VIRTUAL_STAGING_720',
  VirtualStagingCommercial = 'VIRTUAL_STAGING_COMMERCIAL',
  VirtaulStagingCommercial720 = 'VIRTUAL_STAGING_COMMERCIAL_720',
  CombineImages = 'COMBINE_IMAGES',
  Arial = 'ARIAL',
  PersonInfo = 'PERSON_INFO',
  IntroOutro = 'INTRO_OUTRO',
  VidibiliComment = 'VIDIBILI_COMMENT',
  SpeakInput = 'SPEAK_INPUT',
  ComText = 'COMTEXT',
  BeforeAfter = 'BEFORE_AFTER',
  NoStyleText = 'TEXT-STYLE',
  SoMe = 'SOCIAL_MEDIA',
  SoMeSlideshow = 'SOCIAL_MEDIA_SLIDESHOW',
  SomeGif = 'SOCIAL_MEDIA_GIF',
  ImageSelector = 'IMAGE_SELECTOR',
  WhiteboxImageSelector = 'WHITEBOX_IMAGE_SELECTOR',
  AITextInput = 'AITextInput',
  AITextOverview = 'AITextOverview',
}

export enum BookingTypes {
  BookingPrimaryConfirmed = 'PRIMARYCONFIRMED',
  BookingSecondaryConfirmed = 'SECONDARYCONFIRMED',
  Booking3rdParty = '3RDPARTY',
  Booking3rdPartyPrimaryConfirmed = '3RDPARTYPRIMARYCONFIRMED',
  Booking3rdPartySecondaryConfirmed = '3RDPARTYSECONDARYCONFIRMED',
  BookingEsoft = 'ESOFT',
}

export type VirtualStagingType = 'Residential' | 'Commercial';

export interface ITaskBookingContact {
  email: string;
  phone: string;
  name: string;
  secondaryPhone: string;
  type: string;
  chosen?: boolean;
}

export interface IDatahubTask {
  _dto: IDatahubTaskDto;
  branchId: IBranch['id'];
  branchName: IBranch['name'];
  caseId: IDatahubCase['id'];
  caseReference: IDatahubCase['reference'];
  createdAt: Date;
  id: string;
  productName: string;
  status: TaskStatus;
  type: TaskCategory;
  address?: string;
  orderId: ISimplifiedOrder['id'];
  orderlineId: IDatahubOrderLine['id'];
  salesId: ISimplifiedOrder['salesId'];
  contactPersonId: IUser['id'];
  notes?: {
    text?: string;
  }[];
}

export interface IGetTaskListParams extends CommonGetListType<
  'createdAt' | 'branchName' | 'caseId' | 'type' | 'status' | 'productName' | 'caseReference' | 'address'
> {
  branchIds?: string[];
  category?: TaskCategory[];
  status?: TaskStatus[];
  signal?: AbortSignal;
}

export interface IGetTasksListResponse extends PaginatedListResponseDeprecatedType<IDatahubTask> {}

export interface ITaskFilters {
  categories: TaskCategory[];
  statuses: TaskStatus[];
  branches: {
    id: string;
    name: string;
  }[];
}

export interface ITaskInputTextForm {
  inputArea: string;
  propertyType: string;
  radioGroup?: string;
  isInterviewRequired: boolean;
  responsibleAgent: string;
  contactType?: string;
  phone?: string;
  secondaryPhone?: string;
  email?: string;
  contactName?: string;
}

export interface ITaskInputText {
  email: IUser['email'];
  name: IUser['fullName'];
  taskId: IDatahubTask['id'];
}

export interface ITaskCopywriteText {
  [key: string]: string | undefined;
}

export interface ITaskCopywriteTextWithReview extends ITaskCopywriteText {
  comment: string;
  feedbackType: FeedbackType;
}

export interface ITaskNote {
  noteText: string;
  lineNum: number;
  salesId: string;
  taskId: number;
}

export interface ITaskCreateCommentParams {
  comment: string;
  taskId: IDatahubTask['id'];
  email: string;
}

export interface IVirtualStaging {
  roomTypeName: string;
  url: string;
  isSelected: boolean;
  interiorOptions: {
    name: string;
    url: string;
    isSelected: boolean;
  }[];
}

export interface OrderVirtualStagingProps {
  taskId: IDatahubTask['id'];
  materialIds: ISimplifiedVisualMaterial['id'][];
  //clientId: 'string'
  batchId: IDatahubOrderLine['batchId'];
  roomType: IVirtualStaging['roomTypeName'];
  //type: 'string'
  code: IVirtualStaging['interiorOptions'][number]['name'];
  furniture: IVirtualStaging['interiorOptions'][number]['url'];
  label?: string;
  // userEmail: 'string'
}

export interface ISubmitNoteOptions {
  person: string;
}

export interface IRequestObjectsRemoval {
  taskId: IDatahubTask['id'];
  materialId: ISimplifiedVisualMaterial['id'];
  materialUrl: ISimplifiedVisualMaterial['source'];
  comment: string;
}

export interface IPhotographerBookingData {
  start: Date;
  end: Date;
  timeForTask: number;
  info?: {
    time?: Date;
    text: string;
  }[];
  bookingId: string;
  id: string;
  arrival: Date;
  departure: Date;
  photographerName?: string;
  area?: number;
}

export interface ICallProposal {
  primary?: IPhotographerBookingData;
  secondary: IPhotographerBookingData[];
  duration: number;
}

export interface IUpdateBookingTypeParams {
  taskId: IDatahubTask['id'];
  bookingId: string;
  bookingType: BookingTypes;
  responsibleUser: IUser['fullName'];
  person: string;
}

export interface IBeforeAfterParams {
  taskId: IDatahubTask['id'];
  assetIds: [ISimplifiedVisualMaterial['id'], ISimplifiedVisualMaterial['id']];
}

export interface ISomeGifParams {
  taskId: IDatahubTask['id'];
  beforeMaterialId: ISimplifiedVisualMaterial['id'];
  afterMaterialId: ISimplifiedVisualMaterial['id'];
}

export interface IInterviewQuestion {
  answer: string;
  question: string;
  yesOrNo: boolean;
}

export interface IAITextQuestionnaire {
  questions: IInterviewQuestion[];
  isSendEmailAvailable: boolean;
}
